import type { User as GQLItem } from '../../../generated/api.graphql';
import { take } from '../../../util';

/**
 * Declared as a type alias rather than an interface on purpose: `take` types its input as
 * `Partial<Record<keyof T, unknown>>`, and an interface — having no implicit index
 * signature — cannot satisfy that. `Api.saveUser` passes this straight into `take`.
 */
export type User = {
  id?: number;
  name?: string;
  email?: string;
  /**
   * Whether the user has ever signed in. Optional on the domain type even though the schema
   * types it `Boolean!`, because only the selections that ask for it carry one — the profile
   * screens do not.
   */
  active?: boolean;
};

/**
 * The mutation payload types `id` as `ID!`, so it arrives as a string, while `Me.id` and the
 * `UserAttributes.id` this same value is sent back as are both `Int`. Coerce here so callers
 * only ever hold a number.
 *
 * `take<User, GQLItem>` rather than `take<User>`: the second type argument is what makes the key
 * list `keyof GQLItem` instead of a free string, so a field the schema drops or renames — `active`
 * being the one this file would otherwise lose silently — becomes a compile error.
 */
export const fromGql = (item: GQLItem): User => ({
  ...take<User, GQLItem>(item, 'name', 'email', 'active'),
  ...(item.id === undefined || item.id === null ? {} : { id: Number(item.id) }),
});
